// Pure assembly of the `order` text part sent to the import connector (story 194, contract C2.2).
// It never reuses the body of the fabrication order creation row: only the text of each extended value.

const EXTENDED_FIELD = /^extended_column(\d+)$/
const MIN_PRIORITY = 1
const MAX_PRIORITY = 10

/** Number N of an `extended_column<N>` field, or 0 when the field is not one (never from the position). */
export const extendedColumnNumber = (field) => {
  const match = EXTENDED_FIELD.exec(field || '')
  return match ? Number(match[1]) : 0
}

/** V3: a real integer from 1 to 10 (`PriorityConditionChecker` only checks the range, so 5.5 passes there). */
export const isValidPriority = (priority) => Number.isInteger(priority) && priority >= MIN_PRIORITY && priority <= MAX_PRIORITY

/**
 * Text that travels for one extended value: the same text the creation row of the fabrication order table stores
 * (S44, declared by Sun in contrato-sun.md). That row sends a date as a `Date`, which JSON turns into its ISO UTC text;
 * text, numbers (the `<input type=number>` value), yes/no and list options end up as their plain text.
 */
export const extendedValueText = (column, value) => value instanceof Date ? value.toISOString() : String(value)

const isEmptyValue = (value) => value === undefined || value === null || value === ''

/**
 * Entries of `extendedColumns`: one per enabled column with a value; a yes/no column always travels (false at first).
 * @param {Array<{field: string, type?: string}>} extendedColumns columns from useGetExtendedColumns('fabrication_order')
 * @param {Object} extendedValues row values keyed by column field (`extended_column<N>`)
 */
const extendedEntries = (extendedColumns, extendedValues) => {
  const entries = []
  extendedColumns.forEach((column) => {
    const number = extendedColumnNumber(column.field)
    if (number === 0) return
    const isBoolean = column.type === 'boolean'
    let value = extendedValues[column.field]
    if (isBoolean && isEmptyValue(value)) value = false
    if (isEmptyValue(value)) return
    entries.push({ number, value: extendedValueText(column, value) })
  })
  return entries
}

/**
 * Builds the C2.2 `order` object (no other key, ever).
 * @param {Object} row
 * @param {string} row.code Fabrication Order code (trimmed here)
 * @param {number} row.targetDateMs target date, epoch milliseconds
 * @param {number} row.priority integer 1-10
 * @param {string} [row.clientCode] omitted when empty
 * @param {string} row.planificationType code of the selected plan
 * @param {Object} [row.extendedValues] values keyed by column field
 * @param {Array} [extendedColumns] enabled extended column definitions
 */
export const buildConnectorOrder = (row, extendedColumns = []) => {
  const order = {
    code: String(row.code ?? '').trim(),
    targetDate: Math.floor(row.targetDateMs / 1000),
    priority: row.priority,
    planificationType: row.planificationType
  }
  if (!isEmptyValue(row.clientCode)) order.clientCode = row.clientCode
  const entries = extendedEntries(extendedColumns, row.extendedValues ?? {})
  if (entries.length > 0) order.extendedColumns = entries
  return order
}
